import { useState, useEffect, useCallback } from 'react';

/**
 * Execute async function and provide its loading status.
 * `initValue` will be returned immediately after hook call and
 * will be replaced with what `callback` returns when it's done.
 * `callback` is executed each time it changes with previous value in arguments
 * @param {Function} callback async function to execute
 * @param {*} initValue initial value for data returned
 * @returns {[<*>, <Boolean>]} first - return value of `callback` or `initValue`
 *                             second - whether promise from `callback` is completed
 */
const useLoading = (callback, initValue) => {
    const [data, setData] = useState(initValue);
    const [isLoading, setIsLoading] = useState(false);

    const execute = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await callback(data);
            if (result) {
                setData(result);
            }
        } catch (e) {
            console.error(e);
        }
        setIsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callback]);
    // ^^ no dependancy on `data` since we don't want to
    // recalculate when it's changing in iterative fashion

    useEffect(() => {
        execute();
    }, [execute]);

    return [data, isLoading];
};

export default useLoading;
